import React, { useRef, memo, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface PhoneInputFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  label?: string;
  validateFn?: (value: string) => boolean;
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = memo(({
  id,
  value,
  onChange,
  required = false,
  hasError = false,
  errorMessage = 'Please enter a valid phone number with country code',
  label,
  validateFn
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  
  // This is critical: don't update the input value through React's state during typing
  // Instead, let the phone input library handle its own internal state
  // We only listen to changes and report them back through onChange
  const handlePhoneChange = (newValue: string | undefined) => {
    if (newValue !== undefined) {
      onChange(newValue);
    }
  };

  // Manually validate phone number format - ensure it has country code and is valid format
  const isValidPhoneNumber = (phone: string): boolean => {
    // Basic validation: starts with +, has country code and at least 8 digits total
    return Boolean(phone && /^\+\d{1,4}\d{8,}$/.test(phone.replace(/\s+/g, '')));
  };
  
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-2" style={{ color: 'var(--text-color, #000000)' }}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={`${hasError ? 'phone-input-error' : ''}`}>
        {/* We're using a direct implementation with fewer re-renders */}
        <PhoneInput
          international
          countryCallingCodeEditable={false}
          defaultCountry="US"
          value={value}
          onChange={handlePhoneChange}
          className="w-full"
          smartCaret
          addInternationalOption={false}
          focusInputOnCountrySelection
          numberInputProps={{
            id,
            name: id,
            className: `w-full p-3 focus:outline-none focus:ring-0 transition-all bg-transparent ${
              hasError 
                ? 'border-b border-red-500' 
                : 'border-[var(--primary-color)]'
            }`,
            style: {
              borderTopWidth: '0',
              borderLeftWidth: '0',
              borderRightWidth: '0',
              borderBottomWidth: '1px',
              borderRadius: '0',
              paddingLeft: '0',
              paddingRight: '0',
              backgroundColor: 'transparent',
              color: 'var(--text-color, #000000)'
            }
          }}
          style={{
            '--PhoneInput-color--focus': 'var(--primary-color, #6366F1)',
            '--PhoneInputCountryFlag-borderColor--focus': 'var(--primary-color, #6366F1)',
            '--PhoneInputCountrySelectArrow-color--focus': 'var(--primary-color, #6366F1)',
          }}
        />
      </div>
      {/* Add a custom style element to target the input on focus */}
      <style>
        {`
        .PhoneInputInput:focus {
          border-bottom-color: var(--primary-color, #6366F1) !important;
        }
        `}
      </style>
      {hasError && (
        <p className="mt-2 text-sm text-red-600 flex items-center">
          <ExclamationCircleIcon className="h-4 w-4 mr-1" />
          {!value 
            ? 'This field is required'
            : (!isValidPhoneNumber(value) 
                ? 'Please enter a valid phone number with country code (e.g., +1234567890)'
                : errorMessage)}
        </p>
      )}
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';

export default PhoneInputField; 